<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <!-- API Loading Overlay with Animation -->
    <transition name="loading-overlay">
      <div v-if="isSubmitting" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">
                <i class="fas fa-file-invoice"></i>
              </div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="loading-tips">
            <p class="tip-text">{{ currentTip }}</p>
          </div>
        </div>
      </div>
    </transition>

    <div class="modal-container glass-modal-container modal-dispute">
      <div class="modal-header glass-modal-header">
        <h3>Create New Dispute</h3>
        <button class="modal-close" @click="close">×</button>
      </div>
      <div class="modal-body">
        <!-- Search Section -->
        <div class="dispute-search-section">
          <div class="search-tabs">
            <button 
              :class="['search-tab glass-tab', { active: searchType === 'invoice' }]"
              @click="searchType = 'invoice'; searchPerformed = false; searchResults = []"
            >
              Search by Invoice #
            </button>
            <button 
              :class="['search-tab glass-tab', { active: searchType === 'customer' }]"
              @click="searchType = 'customer'; searchPerformed = false; searchResults = []"
            >
              Search by Customer
            </button>
          </div>
          
          <div class="search-input-group">
            <input 
              v-if="searchType === 'invoice'"
              v-model="invoiceSearchQuery"
              type="text"
              placeholder="Enter invoice number..."
              class="modal-input glass-input"
              @keyup.enter="searchInvoices"
            />
            <input 
              v-else
              v-model="customerSearchQuery"
              type="text"
              placeholder="Enter customer name, phone, or email..."
              class="modal-input glass-input"
              @keyup.enter="searchInvoices"
            />
            <button @click="searchInvoices" class="search-btn glass-btn-primary">Search</button>
          </div>
          
          <div v-if="searching" class="searching-indicator">
            <i class="fas fa-spinner fa-spin"></i> Searching...
          </div>
          
          <div v-else-if="searchPerformed" class="search-results">
            <div class="results-header" v-if="searchResults.length > 0">
              Found {{ searchResults.length }} completed invoice(s)
            </div>
            <div class="results-list">
              <div 
                v-for="invoice in searchResults" 
                :key="invoice.invoice_id"
                :class="['result-item glass-result-item', { selected: selectedInvoice && selectedInvoice.invoice_id === invoice.invoice_id }]"
                @click="selectInvoice(invoice)"
              >
                <div class="result-invoice">Invoice: {{ invoice.invoice_id }}</div>
                <div class="result-dispute-type">
                  Dispute: {{ invoice.dispute_type || 'No dispute' }}
                </div>
              </div>
            </div>
            <div v-if="searchResults.length === 0" class="no-results">
              No completed invoices found. Only invoices marked as 'completed' can be disputed.
            </div>
          </div>
        </div>
        
        <!-- Invoice Information - Collapsible -->
        <div v-if="selectedInvoice" class="selected-invoice-info glass-card-inner">
          <div class="invoice-header" @click="toggleInvoiceInfo">
            <h4>Invoice & Dispute Information</h4>
            <span class="toggle-icon">{{ invoiceInfoExpanded ? '−' : '+' }}</span>
          </div>
          
          <div v-show="invoiceInfoExpanded" class="invoice-content">
            <div class="info-section">
              <h5>Invoice Details</h5>
              <p><strong>Invoice #:</strong> {{ selectedInvoice.invoice_id }}</p>
              <p><strong>Customer:</strong> {{ selectedInvoice.customer_name }}</p>
              <p><strong>Total Amount:</strong> ${{ parseFloat(selectedInvoice.total_amount || 0).toFixed(2) }}</p>
              <p><strong>Invoice Date:</strong> {{ selectedInvoice.invoice_date || 'N/A' }}</p>
            </div>
            
            <div class="info-section">
              <h5>Dispute Information</h5>
              <p><strong>Dispute Type:</strong> {{ selectedInvoice.dispute_type || 'Not disputed yet' }}</p>
              <p v-if="selectedInvoice.credit_card_company"><strong>Credit Card Company:</strong> {{ selectedInvoice.credit_card_company }}</p>
              <p v-if="selectedInvoice.dispute_company_name"><strong>Company Name:</strong> {{ selectedInvoice.dispute_company_name }}</p>
              <p><strong>Customer Statement:</strong> {{ selectedInvoice.customer_statement || 'N/A' }}</p>
              <p><strong>Issue Type:</strong> {{ selectedInvoice.issue_type || 'N/A' }}</p>
              <p><strong>Resolution Status:</strong> {{ selectedInvoice.resolution || 'Pending' }}</p>
              <p><strong>Money Lost:</strong> {{ selectedInvoice.money_lost ? 'Yes' : 'No' }}</p>
              <p v-if="selectedInvoice.money_amount"><strong>Money Amount:</strong> ${{ parseFloat(selectedInvoice.money_amount).toFixed(2) }}</p>
              <p v-if="selectedInvoice.details"><strong>Details:</strong> {{ selectedInvoice.details }}</p>
              <p v-if="selectedInvoice.dispute_date"><strong>Dispute Date:</strong> {{ formatDate(selectedInvoice.dispute_date) }}</p>
              <p v-if="selectedInvoice.resolution_date"><strong>Resolution Date:</strong> {{ formatDate(selectedInvoice.resolution_date) }}</p>
            </div>
          </div>
        </div>
        
        <!-- Create New Dispute Form -->
        <div class="dispute-form-section">
          <h6 v-if="!selectedInvoice">Search for an invoice to create a dispute</h6>
          <template v-else>
            <div class="form-group">
              <label>Dispute Type *</label>
              <select v-model="form.dispute_type" @change="onDisputeTypeChange" class="modal-select glass-select">
                <option :value="null">Select dispute type</option>
                <option value="customer">Customer Dispute</option>
                <option value="credit_card">Credit Card Company</option>
                <option value="snap_finance">Finance Company</option>
                <option value="merchant_service">Merchant Service</option>
                <option value="attorney">Attorney</option>
                <option value="court">Court</option>
              </select>
            </div>
            
            <div class="form-group" v-if="form.dispute_type === 'credit_card'">
              <label>Credit Card Company *</label>
              <input type="text" v-model="form.credit_card_company" class="modal-input glass-input" placeholder="e.g., Visa, Mastercard" />
            </div>
            
            <div class="form-group" v-if="isFinanceType && form.dispute_type !== 'credit_card'">
              <label>{{ companyLabel }} *</label>
              <input type="text" v-model="form.dispute_company_name" class="modal-input glass-input" :placeholder="companyPlaceholder" />
            </div>
            
            <div class="form-group">
              <label>Customer Statement *</label>
              <div class="radio-group">
                <label class="radio-label glass-radio">
                  <input type="radio" value="received" v-model="form.customer_statement" /> Received
                </label>
                <label class="radio-label glass-radio">
                  <input type="radio" value="not_received" v-model="form.customer_statement" /> Not Received
                </label>
              </div>
            </div>
            
            <div class="form-group">
              <label>Issue Type *</label>
              <select v-model="form.issue_type" class="modal-select glass-select">
                <option :value="null">Select issue type</option>
                <option value="fraud_charges">Fraud Charges</option>
                <option value="broken_merchandise">Broken Merchandise</option>
                <option value="wrong_item">Wrong Item</option>
                <option value="missing_item">Missing Item</option>
                <option value="not_as_described">Not as Described</option>
                <option value="delivery_issue">Delivery Issue</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>Resolution Status</label>
              <select v-model="form.resolution" class="modal-select glass-select">
                <option value="pending">Pending</option>
                <option value="customer_favor">Customer's Favor</option>
                <option value="business_favor">Business's Favor</option>
                <option value="partial_refund">Partial Refund</option>
                <option value="full_refund">Full Refund</option>
                <option value="replacement">Replacement</option>
                <option value="chargeback_won">Chargeback Won</option>
                <option value="chargeback_lost">Chargeback Lost</option>
                <option value="legal_action">Legal Action</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>Money Lost?</label>
              <div class="radio-group">
                <label class="radio-label glass-radio">
                  <input type="radio" :value="true" v-model="form.money_lost" /> Yes
                </label>
                <label class="radio-label glass-radio">
                  <input type="radio" :value="false" v-model="form.money_lost" /> No
                </label>
              </div>
            </div>
            
            <!-- Money Amount Field -->
            <div class="form-group" v-if="form.money_lost">
              <label>Money Lost Amount *</label>
              <div class="money-input-wrapper">
                <span class="currency-symbol">$</span>
                <input 
                  type="number" 
                  v-model="form.money_amount" 
                  step="0.01" 
                  min="0.01"
                  class="modal-input glass-input money-amount-input" 
                  placeholder="0.00"
                />
              </div>
              <p class="field-hint">Please enter the exact amount of money lost (e.g., 49.99)</p>
            </div>
            
            <div class="form-group">
              <label>Dispute Details</label>
              <textarea v-model="form.details" rows="3" class="modal-textarea glass-input" placeholder="Enter any additional details..."></textarea>
            </div>
            
            <div class="form-group">
              <label>Upload Evidence (Image)</label>
              <input type="file" @change="handleImageUpload" accept="image/*" class="file-input" />
              <div v-if="form.image_preview" class="image-preview">
                <img :src="form.image_preview" alt="Preview" />
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="modal-footer glass-modal-footer">
        <button class="btn-secondary glass-btn" @click="close" :disabled="isSubmitting">Cancel</button>
        <button class="btn-primary glass-btn-primary" @click="submit" :disabled="!canSubmit || isSubmitting">
          <span v-if="!isSubmitting">Create Dispute</span>
          <span v-else class="btn-loading">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Creating...
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Requestor from "@/utilities/Requestor"

export default {
  name: 'CreateDisputeModal',
  props: {
    visible: { type: Boolean, default: false },
    financeCompanies: { type: Array, default: () => [] }
  },
  data() {
    return {
      searchType: 'invoice',
      invoiceSearchQuery: '',
      customerSearchQuery: '',
      searchResults: [],
      selectedInvoice: null,
      searching: false,
      searchPerformed: false,
      isSubmitting: false,
      invoiceInfoExpanded: true, // Collapsible state
      loadingMessage: 'Creating Dispute',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      tips: [
        "📝 Preparing dispute details...",
        "🔍 Verifying invoice information...",
        "📄 Generating dispute documentation...",
        "💾 Saving dispute to database...",
        "📨 Sending notification...",
        "✅ Finalizing dispute creation..."
      ],
      form: {
        invoice_id: null,
        dispute_type: null,
        credit_card_company: null,
        dispute_company_name: null,
        customer_statement: null,
        issue_type: null,
        resolution: 'pending',
        money_lost: false,
        money_amount: null,
        details: '',
        image: null,
        image_preview: null
      }
    }
  },
  computed: {
    isFinanceType() {
      const type = this.form.dispute_type
      return type === 'snap_finance' || type === 'finance' || type === 'merchant_service' || type === 'attorney' || type === 'court'
    },
    companyLabel() {
      const labels = {
        'snap_finance': 'Finance Company Name',
        'merchant_service': 'Merchant Service Name',
        'attorney': 'Attorney/Law Firm Name',
        'court': 'Court Name'
      }
      return labels[this.form.dispute_type] || 'Company Name'
    },
    companyPlaceholder() {
      const placeholders = {
        'snap_finance': 'e.g., Snap Finance, Ally Financial',
        'merchant_service': 'e.g., Stripe, PayPal, Square',
        'attorney': 'e.g., Law Offices of John Smith',
        'court': 'e.g., Los Angeles Superior Court'
      }
      return placeholders[this.form.dispute_type] || 'Enter company name'
    },
    currentTip() {
      return this.tips[this.currentTipIndex] || this.tips[0];
    },
    canSubmit() {
      if (!this.selectedInvoice || !this.form.dispute_type) return false
      
      if (this.form.dispute_type === 'customer') {
        if (!this.form.customer_statement || !this.form.issue_type) return false
      }
      
      if (this.form.dispute_type === 'credit_card') {
        if (!this.form.credit_card_company) return false
      }
      
      if (this.isFinanceType) {
        if (!this.form.dispute_company_name) return false
      }
      
      if (this.form.money_lost) {
        if (!this.form.money_amount || parseFloat(this.form.money_amount) <= 0) {
          return false
        }
      }
      
      return true
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetForm()
        this.isSubmitting = false
        this.invoiceInfoExpanded = true
        this.stopLoadingAnimation()
      }
    }
  },
  beforeDestroy() {
    this.stopLoadingAnimation()
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },
    
    // Toggle invoice info collapsible
    toggleInvoiceInfo() {
      this.invoiceInfoExpanded = !this.invoiceInfoExpanded
    },
    
    // ========== LOADING ANIMATION METHODS ==========
    startLoadingAnimation(message = 'Creating Dispute') {
      this.isSubmitting = true
      this.loadingMessage = message
      this.loadingProgress = 0
      this.currentTipIndex = 0
      
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10
          if (this.loadingProgress > 90) this.loadingProgress = 90
        }
      }, 300)
      
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++
        }
      }, 2000)
      
      let messages = ['Creating Dispute', 'Verifying Details', 'Processing Request', 'Saving Data', 'Finalizing']
      let msgIndex = 0
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++
          this.loadingMessage = messages[msgIndex]
        }
      }, 1800)
    },
    
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval)
      if (this.tipInterval) clearInterval(this.tipInterval)
      if (this.msgInterval) clearInterval(this.msgInterval)
      
      if (success) {
        this.loadingProgress = 100
        setTimeout(() => {
          this.isSubmitting = false
        }, 500)
      } else {
        this.isSubmitting = false
      }
    },
    
    async searchInvoices() {
      const searchValue = this.searchType === 'invoice' ? this.invoiceSearchQuery : this.customerSearchQuery
      if (!searchValue.trim()) return
      
      this.searching = true
      this.searchPerformed = true
      
      const data = {
        search_type: this.searchType,
        search_value: searchValue.trim()
      }
      
      try {
        const response = await Requestor.getAsyncP("/api/sales/search_completed_invoices/", data)
        if (response.data.error) {
          this.searchResults = []
        } else {
          this.searchResults = response.data.result || []
        }
      } catch (error) {
        console.error('Error searching invoices:', error)
        this.searchResults = []
      } finally {
        this.searching = false
      }
    },
    
    selectInvoice(invoice) {
      this.selectedInvoice = invoice
      this.form.invoice_id = invoice.invoice_id
      
      if (invoice.dispute_type) {
        this.form.dispute_type = invoice.dispute_type
        this.form.credit_card_company = invoice.credit_card_company || null
        this.form.dispute_company_name = invoice.dispute_company_name || null
        this.form.customer_statement = invoice.customer_statement || null
        this.form.issue_type = invoice.issue_type || null
        this.form.resolution = invoice.resolution || 'pending'
        this.form.money_lost = invoice.money_lost || false
        this.form.money_amount = invoice.money_amount || null
        this.form.details = invoice.details || ''
      }
    },
    
    onDisputeTypeChange() {
      if (this.form.dispute_type === 'customer') {
        this.form.dispute_company_name = null
        this.form.credit_card_company = null
      } else if (this.form.dispute_type === 'credit_card') {
        this.form.dispute_company_name = null
      } else if (this.isFinanceType) {
        this.form.credit_card_company = null
        if (!this.form.dispute_company_name) {
          this.form.dispute_company_name = ''
        }
      }
    },
    
    handleImageUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.form.image = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.image_preview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    
    resetForm() {
      this.searchType = 'invoice'
      this.invoiceSearchQuery = ''
      this.customerSearchQuery = ''
      this.searchResults = []
      this.selectedInvoice = null
      this.searchPerformed = false
      this.invoiceInfoExpanded = true
      this.form = {
        invoice_id: null,
        dispute_type: null,
        credit_card_company: null,
        dispute_company_name: null,
        customer_statement: null,
        issue_type: null,
        resolution: 'pending',
        money_lost: false,
        money_amount: null,
        details: '',
        image: null,
        image_preview: null
      }
    },
    
    async submit() {
      if (!this.canSubmit || this.isSubmitting) return
      
      // Start loading animation
      this.startLoadingAnimation('Creating Dispute')
      
      const formData = new FormData()
      formData.append("invoice_id", this.form.invoice_id)
      formData.append("dispute_type", this.form.dispute_type)
      
      if (this.form.dispute_type === 'credit_card') {
        formData.append("credit_card_company", this.form.credit_card_company || '')
      } else if (this.form.dispute_type !== 'customer') {
        if (!this.form.dispute_company_name) {
          this.$emit('error', 'Company name is required for this dispute type')
          this.stopLoadingAnimation(false)
          return
        }
        formData.append("dispute_company_name", this.form.dispute_company_name)
      }
      
      if (this.form.dispute_type === 'customer') {
        formData.append("customer_statement", this.form.customer_statement)
        formData.append("issue_type", this.form.issue_type)
      } else {
        formData.append("customer_statement", this.form.customer_statement || 'received')
        formData.append("issue_type", this.form.issue_type || 'other')
      }
      
      formData.append("resolution", this.form.resolution)
      formData.append("money_lost", this.form.money_lost)
      
      if (this.form.money_lost && this.form.money_amount) {
        formData.append("money_amount", this.form.money_amount)
      }
      
      formData.append("details", this.form.details || "")
      
      if (this.form.image) {
        formData.append("image", this.form.image)
      }
      
      try {
        const response = await Requestor.postAsyncP("/api/sales/create_dispute/", formData)
        if (response.data.error) {
          this.$emit('error', response.data.error)
          this.stopLoadingAnimation(false)
        } else {
          this.$emit('created')
          this.stopLoadingAnimation(true)
          setTimeout(() => {
            this.close()
          }, 300)
        }
      } catch (error) {
        console.error('Error creating dispute:', error)
        this.$emit('error', error.response.data.error || 'Error creating dispute')
        this.stopLoadingAnimation(false)
      }
    },
    
    close() {
      this.stopLoadingAnimation(false)
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* ========== LOADING OVERLAY STYLES ========== */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay-enter-active {
  animation: fadeIn 0.3s ease;
}

.loading-overlay-leave-active {
  animation: fadeOut 0.3s ease;
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  max-width: 450px;
  width: 90%;
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 24px;
  flex-shrink: 0;
}

.loading-ring {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 28px;
  font-weight: bold;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

.loading-logo i {
  font-size: 32px;
  background: linear-gradient(135deg, #4299e1, #48bb78);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.2rem;
  margin-bottom: 6px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 5px;
  height: 5px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

.loading-progress {
  width: 80%;
  height: 3px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto 20px auto;
  flex-shrink: 0;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-tips {
  margin-top: 10px;
  flex-shrink: 0;
  min-height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tip-text {
  color: #718096;
  font-size: 0.85rem;
  animation: tipFade 0.5s ease;
  margin: 0;
  padding: 0 15px;
  line-height: 1.4;
}

@keyframes tipFade {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* ========== COLLAPSIBLE INVOICE INFO ========== */
.selected-invoice-info {
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  padding: 4px 0;
}

.invoice-header:hover {
  opacity: 0.8;
}

.invoice-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.toggle-icon {
  font-size: 20px;
  font-weight: 300;
  color: #64748b;
  transition: transform 0.3s ease;
}

.invoice-content {
  margin-top: 12px;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-section {
  margin-bottom: 20px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.info-section:last-child {
  margin-bottom: 0;
}

.info-section h5 {
  margin-bottom: 12px;
  color: #334155;
  font-weight: 600;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 6px;
}

.info-section p {
  margin: 8px 0;
  font-size: 14px;
}

/* ========== BUTTON LOADING STYLES ========== */
.btn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 0.2em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* ========== MONEY INPUT STYLES ========== */
.money-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.currency-symbol {
  position: absolute;
  left: 14px;
  font-size: 18px;
  font-weight: 600;
  color: #64748b;
  pointer-events: none;
}

.money-amount-input {
  padding-left: 32px !important;
  font-size: 16px;
  font-weight: 600;
}

.money-amount-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.money-amount-input::-webkit-inner-spin-button,
.money-amount-input::-webkit-outer-spin-button {
  opacity: 0.5;
  height: 30px;
}

.money-amount-input::-webkit-inner-spin-button:hover {
  opacity: 1;
}

.field-hint {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

/* ========== MODAL STYLES ========== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease;
  margin: auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 24px 24px 0 0;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #475569;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0 0 24px 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.modal-input,
.modal-select,
.modal-textarea {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s;
  color: #1e293b;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 10px;
}

.modal-textarea {
  resize: vertical;
}

.radio-group {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.btn-secondary {
  background: rgba(241, 245, 249, 0.9);
  color: #475569;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dispute-search-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.search-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-tab {
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  background: transparent;
  border: 1px solid #e2e8f0;
}

.search-tab.active {
  background: #1e293b;
  color: white;
  border-color: #1e293b;
}

.search-input-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.search-input-group input {
  flex: 1;
  min-width: 200px;
}

.search-btn {
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
}

.search-results {
  margin-top: 16px;
  max-height: 250px;
  overflow-y: auto;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-item {
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: #f8fafc;
}

.result-item.selected {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid #3b82f6;
}

.image-preview {
  margin-top: 12px;
}

.image-preview img {
  max-width: 200px;
  max-height: 150px;
  border-radius: 8px;
}

.result-dispute-type {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

/* Responsive */
@media (max-width: 768px) {
  .loading-card {
    padding: 30px 20px;
  }
  
  .loading-tips {
    min-height: 55px;
  }
  
  .tip-text {
    font-size: 0.75rem;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/components/CreateDisputeModal.vue